import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getRealtimeUrl } from '@/config/api';
import { useSession } from '@/contexts/SessionContext';
import { CentrifugoClient, type RealtimeStatus } from '@/services/realtime/centrifugoClient';
import { ensureFreshAccessToken } from '@/services/session';

/**
 * Живое соединение с Centrifugo. Поднимается после входа, закрывается при выходе.
 * Его состояние и есть индикатор «онлайн» на панели.
 */

interface RealtimeContextValue {
  status: RealtimeStatus;
  isOnline: boolean;
  /** Когда сервер в последний раз что-то прислал. */
  lastActivityAt: Date | null;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

interface RealtimeProviderProps {
  children: ReactNode;
}

export function RealtimeProvider({ children }: RealtimeProviderProps) {
  const { isAuthenticated } = useSession();
  const [status, setStatus] = useState<RealtimeStatus>('disconnected');
  const [lastActivityAt, setLastActivityAt] = useState<Date | null>(null);
  const clientRef = useRef<CentrifugoClient | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      clientRef.current?.stop();
      clientRef.current = null;
      setStatus('disconnected');
      setLastActivityAt(null);
      return;
    }

    const client = new CentrifugoClient({
      url: getRealtimeUrl(),
      getToken: ensureFreshAccessToken,
      onStatusChange: setStatus,
      onActivity: setLastActivityAt,
    });

    clientRef.current = client;
    client.start();

    return () => {
      client.stop();
      clientRef.current = null;
    };
  }, [isAuthenticated]);

  const value = useMemo<RealtimeContextValue>(
    () => ({ status, isOnline: status === 'connected', lastActivityAt }),
    [status, lastActivityAt],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeContextValue {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within RealtimeProvider');
  }
  return context;
}
